<template>
    <CardContentPanel icon="warning-triangle" v-bind="{action, index}">
        <template #title="{month}">{{ $t('projectclient.projectview.activity.pending.actions.LOT_PRE_PAYMENT_OVERDUE.title', {0: month}) }}</template>
        <template #body>
            <p>{{ getLabel }}</p>
            <VJoyHighlight v-if="isPaymentInProgress" :accent="true" level="info">
                <template #default>
                    {{ $t('projectclient.projectview.activity.pending.actions.LOT_PRE_PAYMENT_OVERDUE.awaitingPayment') }}
                </template>
            </VJoyHighlight>
        </template>
        <template #actions>
            <NuxtLink
                :to="`${host}/project-payment/${projectInformationStore.projectInformation.projectId}/lot/${action.extraData.lotId}?origin=project-view`"
                external
                data-testid="pending-action-card-cta-primary"
            >
                <VJoyButton :variant="ctaVariant" size="small">
                    {{ $t('projectclient.projectview.activity.pending.actions.LOT_PRE_PAYMENT_OVERDUE.action.primary') }}
                </VJoyButton>
            </NuxtLink>
        </template>
    </CardContentPanel>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {parse} from 'tinyduration';
    import {useLogger, useTranslation, useFormat, useGuestHost} from '#imports';
    import type {TJoyButtonVariants} from '@maltjoy/core-vue';
    import {VJoyButton, VJoyHighlight} from '@maltjoy/core-vue';
    import {useProjectInformationStore} from '~/common/stores';
    import CardContentPanel from '../CardContentPanel.vue';
    import type {ActivityPendingActionByType} from '@/features/project-view';

    const props = defineProps<{
        action: ActivityPendingActionByType<'LOT_PRE_PAYMENT_OVERDUE'>;
        index: number;
    }>();

    const projectInformationStore = useProjectInformationStore();
    const logger = useLogger();
    const {t} = useTranslation();
    const {formatAmount} = useFormat();

    const host = useGuestHost();

    const isPaymentInProgress = computed<boolean>(() => props.action.extraData.paymentInProgress === 'true');

    const ctaVariant = computed<TJoyButtonVariants>(() => (isPaymentInProgress.value ? 'secondary' : 'main'));

    const calculatePastDue = computed<number>(() => {
        if (props.action.extraData.dueSince) {
            return Math.abs(parse(props.action.extraData.dueSince).days ?? 0);
        }
        logger.error(
            `Missing "dueSince" extraData from LOT_PRE_PAYMENT_OVERDUE pending action in project ${projectInformationStore.projectInformation.projectId}`,
        );
        return 0;
    });

    const formattedAmount = computed<string>(() => {
        const amount = props.action.extraData.remaining;
        return !isNaN(amount) ? formatAmount(projectInformationStore.projectInformation.contract.currency, amount, {decimals: 2}) : '-';
    });

    const getLabel = computed(() => {
        const {extraData} = props.action;
        const translationKey = `projectclient.projectview.activity.pending.actions.LOT_PRE_PAYMENT_OVERDUE.description`;
        if (typeof extraData.billingContact === 'undefined') {
            return t(translationKey, {dueSince: calculatePastDue.value, remaining: formattedAmount.value});
        } else {
            return t(`${translationKey}.with.billing.email`, {
                dueSince: calculatePastDue.value,
                billingEmail: extraData.billingContact,
                remaining: formattedAmount.value,
            });
        }
    });
</script>
